import { itemChannelScope, modelFamilyFromSubject, explicitlyClassified } from './janetSourceScope.js'
// src/lib/janetNoticings.js — "Janet noticed": two facts in the record that
// disagree (Aaron, 2026-10-12, after ChatGPT's assistant caught that his flight
// home is Nov 3 while the hotel booking ends Nov 2).
//
// Pure. The worker's mismatch pass (api/_lib/janet-noticings.js), the morning
// email, Home's block and the chat handoff all read these rules.
//
// A noticing is a QUESTION, never an act. What makes it trustworthy is that
// every fact it rests on is QUOTED from a piece of evidence the pass actually
// read, and the quote is checked against that evidence's text here — a model
// that paraphrases a date wrong, or invents one, produces a noticing that is
// dropped, not shown. Two kinds may rest on one quoted fact plus an absence
// ("no task or calendar entry covers it"), because an absence has nothing to
// quote; every other kind needs two quoted facts from two different sources.

// conflict (Aaron, 2026-10-12, after Dot asked "the Cirque launch overlaps
// your school-pickup block — is pickup covered?"): two commitments at once.
export const NOTICING_KINDS = ['booking_gap', 'date_mismatch', 'conflict', 'calendar_missing', 'deadline_untracked', 'logistics_update', 'other']
export const ABSENCE_KINDS = new Set(['calendar_missing', 'deadline_untracked'])
export const MAX_NOTICINGS = 5
export const NOTICING_STATUSES = ['open', 'acknowledged', 'accepted', 'dismissed', 'superseded']
export const NOTICING_TOPICS = ['travel', 'deadline', 'meeting', 'family', 'other']
// Aaron, 2026-10-12: "text me for urgent travel and deadlines".
export const TEXT_TOPICS = new Set(['travel', 'deadline'])
const TEXT_HORIZON_MS = 7 * 86400000

// Family evidence is internal/push-only even when the model chooses travel
// or deadline. Topic alone cannot expand external authority for the new scope.
export function isFamilyOnlyNoticing(n) {
  if (n?.topic === 'family' || n?.family_only === true) return true
  // New normalized rows persist validated scope in existing source JSON.
  // Legacy, missing and malformed classification is internal-only. Every
  // persisted source must affirmatively permit professional external scope.
  if (!Array.isArray(n?.sources) || !n.sources.length || n.sources.some(s => s?.family_only !== false)) return true
  const evidence = [n?.title, n?.question, ...(n?.facts || []).flatMap(f => [f.quote, f.says, f.source])].filter(Boolean).join(' ')
  return /\b(school|camp|daycare|childcare|after[- ]school|children|kids?|daughter|son|family|permission slips?|parent[- ]teacher)\b/i.test(evidence)
}

/**
 * Is this noticing worth a text? The model flags urgency; this is the
 * deterministic guard on top: travel or a deadline only, flagged urgent, and
 * when it states when it must be settled, that moment is within a week and not
 * long gone. Pure.
 */
export function isTextWorthy(n, now = Date.now()) {
  if (!n?.urgent || !TEXT_TOPICS.has(n.topic) || isFamilyOnlyNoticing(n)) return false
  if (!n.deadline_at) return true
  const t = new Date(n.deadline_at).getTime()
  if (!Number.isFinite(t)) return true
  return t - now <= TEXT_HORIZON_MS && t > now - 86400000
}

/** The text itself: short, plain, and answerable by replying. */
export function noticingText(n) {
  const fact = (n.facts || []).slice(0, 2).map(f => f.says || f.quote).filter(Boolean).join('; ')
  return [`Janet: ${n.title}.`, fact ? `(${fact})` : null, n.question, 'Reply and I\'ll look into it.']
    .filter(Boolean).join(' ').slice(0, 480)
}

const KIND_WORDS = {
  booking_gap: 'Bookings don\'t line up',
  date_mismatch: 'Two records disagree',
  conflict: 'Two things at once',
  calendar_missing: 'Not on your calendar',
  deadline_untracked: 'A deadline with no task',
  logistics_update: 'Meeting update',
  other: 'Worth a look',
}

export function noticingKindLabel(kind) {
  return KIND_WORDS[kind] || KIND_WORDS.other
}

function clean(s, max) {
  const t = String(s ?? '').replace(/\s+/g, ' ').trim()
  return max && t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t
}

/** Lowercased, whitespace and typographic quotes/dashes folded — how quotes are matched. */
export function foldForMatch(s) {
  return String(s ?? '')
    .toLowerCase()
    .replace(/[‘’‚′]/g, "'")
    .replace(/[“”„″]/g, '"')
    .replace(/[–—−]/g, '-')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Does `quote` appear in `text`? Short quotes can't prove anything and fail. */
export function quoteFound(quote, text) {
  const q = foldForMatch(quote).replace(/^["'.…\s]+|["'.…\s]+$/g, '')
  if (q.length < 6) return false
  return foldForMatch(text).includes(q)
}

/** Identity of a noticing across runs: its kind and the records it rests on. */
export function noticingFingerprint(kind, sourceKeys) {
  return `${kind}|${[...new Set(sourceKeys)].sort().join('|')}`
}

/**
 * Shape-check the model's noticings against the evidence.
 *
 * @param {Array} raw  as the tool returned them
 * @param {Map<string,{key,kind,label,url,text,threadId,eventId}>} sources  evidence id → ref (with its full text)
 * @param {{ ruledFingerprints?: Set<string>, max?: number }} opts  fingerprints the principal already
 *        dismissed or took — never raised again
 * @returns {{ noticings: Array, dropped: Array<{title,reason}> }}
 */
export function normalizeNoticings(raw, sources, { ruledFingerprints = new Set(), max = MAX_NOTICINGS, familyEnabled = true, principalId, onScopeUnavailable = () => {} } = {}) {
  const noticings = []
  const dropped = []
  const seen = new Set()
  for (const n of Array.isArray(raw) ? raw : []) {
    if (!n || typeof n !== 'object') continue
    const title = clean(n.title, 160)
    const question = clean(n.question, 400)
    if (!title || !question) { dropped.push({ title: title || '(untitled)', reason: 'missing title or question' }); continue }
    const kind = NOTICING_KINDS.includes(n.kind) ? n.kind : 'other'

    const facts = []
    let badQuote = null
    for (const f of Array.isArray(n.facts) ? n.facts : []) {
      const ref = sources.get(String(f?.source_id || ''))
      if (!ref) { onScopeUnavailable(); badQuote = `cites ${f?.source_id || 'nothing'}, which the pass never read`; break }
      if (!quoteFound(f.quote, ref.text)) { badQuote = `quote not found in ${f.source_id}: "${clean(f.quote, 80)}"`; break }
      facts.push({ source_id: String(f.source_id), quote: clean(f.quote, 300), says: clean(f.says, 200) || null, ref })
    }
    if (badQuote) { dropped.push({ title, reason: badQuote }); continue }

    const distinct = new Set(facts.map(f => f.ref.key))
    const absence = clean(n.absence, 200) || null
    const grounded = ABSENCE_KINDS.has(kind)
      ? facts.length >= 1 && (distinct.size >= 2 || !!absence)
      : distinct.size >= 2
    if (!grounded) { dropped.push({ title, reason: ABSENCE_KINDS.has(kind) ? 'needs a quoted fact and what is missing' : 'needs quoted facts from two different records' }); continue }

    if (kind === 'logistics_update') {
      const mails = facts.filter(f => f.ref.kind === 'email')
      const events = facts.filter(f => f.ref.kind === 'event')
      if (n.subject_scope !== 'professional' || mails.length !== 1 || events.length !== 1 || facts.length !== 2 ||
          mails[0].ref.logisticsEventKey !== events[0].ref.key) {
        dropped.push({ title, reason: 'logistics needs one linked email and its event, classified professional' }); continue
      }
    }

    const fingerprint = noticingFingerprint(kind, [...distinct])
    if (seen.has(fingerprint)) continue
    seen.add(fingerprint)
    if (ruledFingerprints.has(fingerprint)) { dropped.push({ title, reason: 'already ruled on' }); continue }

    const refs = [...new Map(facts.map(f => [f.ref.key, f.ref])).values()]
    const scope = itemChannelScope(refs, principalId, modelFamilyFromSubject(n), n.topic)
    if (!scope.accessAllowed) { onScopeUnavailable(); dropped.push({ title, reason: 'missing trusted source access' }); continue }
    // An explicit "mixed" or "uncertain" is a real answer, excluded while family
    // is off; only a missing classification means the scope was unavailable.
    if (!familyEnabled && scope.family !== false) { if (scope.family === null && !explicitlyClassified(n)) onScopeUnavailable(); dropped.push({ title, reason: 'family feature disabled or classification unavailable' }); continue }
    noticings.push({
      rank: noticings.length + 1,
      kind,
      title,
      question,
      facts: facts.map(f => ({ quote: f.quote, says: f.says, source: f.ref.label })),
      absence: ABSENCE_KINDS.has(kind) ? absence : null,
      suggestion: clean(n.suggestion, 300) || null,
      deadline_at: validDate(n.deadline_at),
      deadline_label: clean(n.deadline_label, 80) || null,
      topic: kind === 'logistics_update' ? 'meeting' : NOTICING_TOPICS.includes(n.topic) ? n.topic : 'other',
      urgent: kind === 'logistics_update' ? false : n.urgent === true,
      fingerprint,
      sources: refs.map(r => ({ family_only: scope.externalFamilyOnly, internal_scope: scope.internal, kind: r.kind, label: r.label, url: r.url || null, thread_id: r.threadId || null, event_id: r.eventId || null })),
    })
    if (noticings.length >= max) break
  }
  return { noticings, dropped }
}

function validDate(v) {
  if (!v) return null
  const d = new Date(v)
  return Number.isFinite(d.getTime()) ? d.toISOString() : null
}

/** Where "Look into it" goes: Janet's chat, which takes the question. */
export function noticingLookPath(id) {
  return `/janet/chat?noticing=${encodeURIComponent(id)}`
}

/** What Janet is handed when the principal asks her to look into one. */
export function noticingQuestion(n) {
  const lines = [`You noticed something: ${n.title}`, '']
  for (const f of n.facts || []) lines.push(`- ${f.source}: "${f.quote}"${f.says ? ` (${f.says})` : ''}`)
  if (n.absence) lines.push(`- ${n.absence}`)
  lines.push('', n.question)
  lines.push(n.suggestion
    ? `Look into it: ${n.suggestion} Check our own records first, search the web if it helps, and tell me what you'd do — don't book, buy or send anything without asking me.`
    : 'Look into it and tell me what you\'d do — check our own records first, search the web if it helps, and don\'t book, buy or send anything without asking me.')
  // Dot's follow-through (Aaron, 2026-10-12): the hotel got a quote request.
  // Janet's version is a draft he approves, never a send.
  lines.push('If fixing it needs a message to a hotel, airline or other vendor, draft that email for me to approve — give them the booking details from the confirmation, ask for a quote or the change terms, and say plainly it is a request, not a change.')
  return lines.join('\n')
}

/** Recent rulings, as the next pass should read them (JUDGMENT-LEDGER §5). */
export function priorNoticingsDigest(rows) {
  const ruled = (rows || []).filter(r => ['acknowledged', 'accepted', 'dismissed'].includes(r.status))
  if (!ruled.length) return ''
  return ruled.slice(0, 20).map(r => `- ${r.status === 'accepted' ? 'LOOKED INTO' : r.status === 'acknowledged' ? 'ACKNOWLEDGED (NOT RESOLVED)' : 'SAID IT WAS FINE'}: ${r.title}`).join('\n')
}
